import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import {
  initializeFirestore,
  getDocFromServer,
  doc,
  Firestore,
  FirestoreSettings
} from 'firebase/firestore';
import { getStorage as getStorageFn, FirebaseStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Core App
const app: FirebaseApp = initializeApp(firebaseConfig);

// Diaspora Optimization: Force Long Polling for stable connections on mobile data
// Critical for Kenyan and Diaspora users on weak 3G/LTE networks
// Prevents connection drops during funeral livestreams and real-time updates
const firestoreSettings: FirestoreSettings = {
  experimentalForceLongPolling: true,
};

// Handle Multiple Databases (if applicable) - Safe databaseId handling
const databaseId = (firebaseConfig as { firestoreDatabaseId?: string }).firestoreDatabaseId;

let _db: Firestore | null = null;
export function getDb(): Firestore {
  if (!_db) {
    _db = (databaseId && databaseId.trim() && databaseId !== '(default)')
      ? initializeFirestore(app, firestoreSettings, databaseId.trim())
      : initializeFirestore(app, firestoreSettings);
  }
  return _db;
}

// Deferred auth initialization: prevents the 90KB Firebase auth iframe
// from loading eagerly on the homepage critical path.
let _auth: Auth | null = null;
export function getAdminAuth(): Auth {
  if (!_auth) {
    _auth = getAuth(app);
    setPersistence(_auth, browserLocalPersistence).catch((error) => {
      console.error('[Auth Persistence Error]', error);
    });
  }
  return _auth;
}

let _storage: FirebaseStorage | null = null;
export function getStorage(): FirebaseStorage {
  if (!_storage) {
    _storage = getStorageFn(app);
  }
  return _storage;
}

/** * ERROR HANDLING & TYPES 
 */
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  timestamp: string;
  userId: string | null;
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);

  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    operationType,
    path,
    timestamp: new Date().toISOString(),
    userId: getAdminAuth().currentUser?.uid || null,
  };

  // Log for debugging but don't break the UI for the user
  console.error(`[Faraja Media Engine] ${operationType} failed at ${path}:`, errInfo);
  throw new Error(`${operationType.toUpperCase()}_FAILED: ${errorMessage}`);
}

/**
 * RESILIENCE UTILITY
 * Retries failed network requests (essential for spotty 3G/LTE)
 */
export async function withRetry<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay * 1.5);
    }
    throw error;
  }
}